import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";

export interface TechficientQuoteRequestPayload {
  amount: number;
  class:
    | "PPNT"
    | "PNT"
    | "SPNT"
    | "SNT"
    | "PT"
    | "SPT"
    | "ST"
    | "PHC"
    | string;
  term: number | null;
  gender: "M" | "F";
  birthDate: string;
  state: string;
  adbRider: boolean;
  wopRider: boolean;
  childRider: "ZERO" | "FIVE" | "TEN";
  productTypeFilter: "Term" | "FinalExpense";
  mode: "Annual" | "Quarterly" | "Monthly" | "Semi-annual";
  /**
   * 2026-06-24 schema: Techficient unified the previously split Term + FE
   * pre-underwriting sub-models into one flat `preUnderwritingRequest`
   * object. See `buildPhcRequest` in QuoteForm for the field names + the
   * `TechficientPreUnderwritingRequest` backend type for the typed shape.
   */
  preUnderwritingRequest?: Record<string, any>;
  /** @deprecated Send `preUnderwritingRequest`. Backend translates if present. */
  preUnderwritingTransmissionModelTerm?: Record<string, any>;
  /** @deprecated Send `preUnderwritingRequest`. Backend translates if present. */
  preUnderwritingTransmissionModelFE?: Record<string, any>;
}

export interface TechficientRunQuoteResponse {
  quoteRequest: {
    id: string;
    techficientQuoteRequestId: string;
    status: string;
  };
  results: Array<{
    id: string;
    techficientQuoteRequestResultId: string;
    carrierName: string;
    carrierCode: string;
    productName: string;
    productCode: string;
    monthlyPremium: number | null;
    annualPremium: number | null;
    healthClassDisplayName?: string;
    isInstantIssue: boolean;
    supportsAppSubmit: boolean;
    /** full_eapp | drop_ticket | quote_only — drives Apply presentation. */
    submittableType?: "full_eapp" | "drop_ticket" | "quote_only";
    rank?: number;
  }>;
}

export interface TechficientProduct {
  productCode: string;
  displayName: string;
  carrierCode: string;
  carrierName: string;
  productType: string;
  supportsAppSubmit: boolean;
  supportsSubmitOnBehalf: boolean;
}

export interface TechficientPushClientResponse {
  techficientClientId: string;
  startQuoteUrl: string;
  clientDetailsUrl: string;
}

export interface TechficientInitiateEappResponse {
  launchUrl: string;
  caseId: string;
  caseRowId: string;
}

export type TechficientCommissionStatus =
  | 'pending'
  | 'advanced'
  | 'charged_back'
  | 'earned';

export interface TechficientCommissionRow {
  status: TechficientCommissionStatus;
  commissionRate?: number;
  expectedAmount?: number;
  advancedAmount?: number;
  chargedBackAmount?: number;
  advancedAt?: string;
  chargedBackAt?: string;
  earnedAt?: string;
  chargedBackReason?: string;
  chargedBackTriggerStatus?: string;
  chargebackWindowDays?: number;
  daysUntilEarned?: number | null;
}

export interface TechficientCaseRow {
  id: string;
  techficientCaseId: string;
  leadId?: string | null;
  /** Lead display name, enriched server-side so the ledger can label rows. */
  leadName?: string | null;
  /** False for submitted/terminal cases — Resume would 400. */
  resumable?: boolean;
  userId: string;
  carrierCode?: string;
  carrierName?: string;
  productCode?: string;
  productName?: string;
  caseStatus?: string;
  caseStatusName?: string;
  caseStatusDisplayReason?: string;
  carrierNativeStatus?: string;
  carrierNativeStatusDescription?: string;
  policyNumber?: string;
  appliedFaceAmount?: number;
  approvedFaceAmount?: number;
  approvedMaxFaceAmount?: number;
  existingCoverage?: number;
  annualizedPremium?: number;
  appliedModalPremium?: number;
  approvedModalPremium?: number;
  appliedHealthClassCode?: string;
  appliedHealthClassName?: string;
  approvedHealthClassCode?: string;
  approvedHealthClassName?: string;
  /** Carrier underwriter's free-text reason for the decision. */
  underwritingDecisionReason?: string;
  launchUrl?: string;
  /**
   * How Resume should present the eApp: 'iframe' for full eApps (embedded
   * Techficient), 'popup' for drop tickets that resume on the carrier's own
   * frame-blocking site (e.g. Banner Life Horizon). Server-computed with the
   * list so the click can open the popup synchronously.
   */
  launchMode?: "iframe" | "popup";
  techficientUpdatedOn?: string;
  updatedAt?: string;
  // Backend join: matching local Policy + PolicyCommission. Null until the
  // case transitions into a "sold" status (then auto-disposition creates a
  // Policy row and TechficientCommissionService records the advance).
  policy?: {
    id: string;
    policyNumber?: string;
    policyStage?: 'underwriting' | 'sold';
    status?: string;
    issuedAt?: string;
  } | null;
  commission?: TechficientCommissionRow | null;
}

export interface TechficientEligibility {
  provisioned: boolean;
  techficientUserId: string | null;
  stateLicenses: Array<{ stateCode: string; licenseNumber: string }>;
  carrierContracts: Array<{
    carrierCode: string;
    productCode?: string;
    writingNumber: string;
  }>;
  /**
   * Roster-imported writing-number prefills for the onboarding wizard —
   * only populated pre-provisioning. writingNumber may be null when the
   * roster cell was a note only (e.g. "Needs release").
   */
  stagedContracts?: Array<{
    carrierCode: string;
    writingNumber: string | null;
    note: string | null;
    rawValue: string | null;
  }>;
  /**
   * Authoritative pre-onboarding match from Techficient's side (agent
   * already exists there via MRFG's BGA appointments). When present, the
   * wizard prefers these licenses + writing numbers over roster guesses.
   */
  techficientPrefill?: {
    techficientUserId: string;
    stateLicenses: Array<{ stateCode: string; licenseNumber: string }>;
    carrierContracts: Array<{
      carrierCode: string;
      productCode?: string;
      writingNumber: string;
    }>;
  } | null;
  lastSyncedAt: string | null;
}

export interface TechficientQuoteHistoryRow {
  id: string;
  createdAt: string;
  leadId: string | null;
  techficientQuoteRequestId: string | null;
  clientName: string | null;
  state: string | null;
  coverageAmount: number | null;
  productType: string | null;
  termYears: number | null;
  /** Full re-quote prefill — everything the original run was rated with. */
  birthDate: string | null;
  gender: "M" | "F" | null;
  zipCode: string | null;
  heightInches: number | null;
  weightPounds: number | null;
  /** Stored preUnderwritingRequest (tobacco/conditions/driving) wire object. */
  phc: Record<string, any> | null;
  status: string;
  carrierCount: number;
  bestMonthlyPremium: number | null;
}

class TechficientService {
  /**
   * Fetch Techficient products. Pass `forceRefresh` to bust the backend's
   * 1-hour cache — useful right after MRFG appoints a new carrier and the
   * agent wants to see it immediately in the wizard.
   */
  async listProducts(
    appSubmitOnly = false,
    forceRefresh = false,
  ): Promise<TechficientProduct[]> {
    const qs: string[] = [];
    if (appSubmitOnly) qs.push("appSubmitOnly=true");
    if (forceRefresh) qs.push("refresh=true");
    const url =
      getApiUrl(endpoint.techficient.products) +
      (qs.length ? `?${qs.join("&")}` : "");
    const resp = await handleApiCall(async () =>
      await api.get<{ items: TechficientProduct[] }>({
        url,
        auth: true,
      }),
    );
    return ((resp as any)?.items as TechficientProduct[]) || [];
  }

  async runQuote(
    body: {
      request: TechficientQuoteRequestPayload;
      leadId?: string | null;
      clientName?: string;
      /** Stored server-side (local-only) so history re-quotes prefill it. */
      zipCode?: string;
    },
  ): Promise<TechficientRunQuoteResponse> {
    return handleApiCall(async () =>
      await api.post<TechficientRunQuoteResponse>({
        url: getApiUrl(endpoint.techficient.quote),
        body,
        auth: true,
      }),
    ) as Promise<TechficientRunQuoteResponse>;
  }

  /**
   * Ensure a lead-less quote has a CRM lead to enroll against — the backend
   * mints one from the quote's own stored inputs. Idempotent.
   */
  async ensureQuoteLead(
    techficientQuoteRequestId: string,
  ): Promise<{ leadId: string; created: boolean }> {
    return handleApiCall(async () =>
      await api.post<{ leadId: string; created: boolean }>({
        url: getApiUrl(
          endpoint.techficient.ensureQuoteLead(techficientQuoteRequestId),
        ),
        auth: true,
      }),
    ) as Promise<{ leadId: string; created: boolean }>;
  }

  async listQuotesForLead(leadId: string) {
    return handleApiCall(async () =>
      await api.get<{ items: any[] }>({
        url: `${getApiUrl(endpoint.techficient.quotes)}?leadId=${encodeURIComponent(leadId)}`,
        auth: true,
      }),
    );
  }

  async pushClient(
    leadId: string,
    client: Record<string, any>,
  ): Promise<TechficientPushClientResponse> {
    // Omit `client` entirely when empty — the backend hydrates from the lead
    // row. Sending `client: {}` would trip nested DTO validation (firstName
    // etc. are required on the nested payload) before the hydration kicks in.
    const body: Record<string, any> = { leadId };
    if (client && Object.keys(client).length > 0) {
      body.client = client;
    }
    return handleApiCall(async () =>
      await api.post<TechficientPushClientResponse>({
        url: getApiUrl(endpoint.techficient.pushClient),
        body,
        auth: true,
      }),
    ) as Promise<TechficientPushClientResponse>;
  }

  async initiateEapp(body: {
    leadId?: string | null;
    techficientClientId: string;
    techficientQuoteRequestId: string;
    techficientQuoteRequestResultId: string;
  }): Promise<TechficientInitiateEappResponse> {
    return handleApiCall(async () =>
      await api.post<TechficientInitiateEappResponse>({
        url: getApiUrl(endpoint.techficient.initiateEapp),
        body,
        auth: true,
      }),
    ) as Promise<TechficientInitiateEappResponse>;
  }

  /**
   * Fully-within drop-ticket submit — backend answers the carrier's
   * application question set from the lead + quote and submits directly.
   * Returns the CARRIER's finish URL (signatures) to open in OUR popup —
   * Techficient's hosted eApp UI (and its tab-spawning handoff buttons)
   * never renders. `submitted: false` + `needsInput` → fall back to the
   * hosted initiate flow.
   */
  async directSubmitEapp(body: {
    leadId: string;
    techficientQuoteRequestId: string;
    techficientQuoteRequestResultId: string;
  }): Promise<{
    submitted: boolean;
    needsInput?: string[];
    launchUrl: string | null;
    caseId: string | null;
    carrier?: string | null;
    product?: string | null;
    launchMode: "popup";
  }> {
    return handleApiCall(async () =>
      await api.post<any>({
        url: getApiUrl(endpoint.techficient.directSubmitEapp),
        body,
        auth: true,
      }),
    ) as Promise<{
      submitted: boolean;
      needsInput?: string[];
      launchUrl: string | null;
      caseId: string | null;
      carrier?: string | null;
      product?: string | null;
      launchMode: "popup";
    }>;
  }

  /**
   * Mint a fresh launch URL for an in-flight case. Takes the TECHFICIENT
   * case id (not our row id). For carrier-pending drop tickets this returns
   * the carrier's own continue/login link (e.g. Banner Horizon) — open it
   * in a popup via the deferred-popup flow, never an iframe.
   */
  async resumeEapp(techficientCaseId: string): Promise<{
    launchUrl: string;
    launchMode?: "iframe" | "popup";
    carrier?: string | null;
    product?: string | null;
  }> {
    return handleApiCall(async () =>
      await api.post<{
        launchUrl: string;
        launchMode?: "iframe" | "popup";
        carrier?: string | null;
        product?: string | null;
      }>({
        url: getApiUrl(endpoint.techficient.resumeEapp(techficientCaseId)),
        auth: true,
      }),
    ) as Promise<{
      launchUrl: string;
      launchMode?: "iframe" | "popup";
      carrier?: string | null;
      product?: string | null;
    }>;
  }

  async listCases(opts: { leadId?: string } = {}): Promise<TechficientCaseRow[]> {
    const url = opts.leadId
      ? `${getApiUrl(endpoint.techficient.cases)}?leadId=${encodeURIComponent(opts.leadId)}`
      : getApiUrl(endpoint.techficient.cases);
    const resp = await handleApiCall(async () =>
      await api.get<{ items: TechficientCaseRow[] }>({ url, auth: true }),
    );
    return ((resp as any)?.items as TechficientCaseRow[]) || [];
  }

  /**
   * Force a status refresh from Techficient (bypasses the 45-min reactive
   * gate on `GET /cases`, but still rate-limited to 1/min per tenant on
   * the backend). Response `.skipped=true` means we hit the 1-min gate;
   * the UI should treat that as "already fresh" rather than an error.
   */
  async syncCasesNow(): Promise<{ processed: number; skipped: boolean }> {
    return handleApiCall(async () =>
      await api.post<{ processed: number; skipped: boolean }>({
        url: getApiUrl(endpoint.techficient.casesSync),
        body: {},
        auth: true,
      }),
    ) as Promise<{ processed: number; skipped: boolean }>;
  }

  /** The agent's own recent quote history (all leads + standalone). */
  async listMyQuotes(): Promise<TechficientQuoteHistoryRow[]> {
    const resp = await handleApiCall(async () =>
      await api.get<{ items: TechficientQuoteHistoryRow[] }>({
        url: getApiUrl(endpoint.techficient.myQuotes),
        auth: true,
      }),
    );
    return ((resp as any)?.items as TechficientQuoteHistoryRow[]) || [];
  }

  async getCase(techficientCaseId: string): Promise<TechficientCaseRow> {
    return handleApiCall(async () =>
      await api.get<TechficientCaseRow>({
        url: getApiUrl(endpoint.techficient.caseById(techficientCaseId)),
        auth: true,
      }),
    ) as Promise<TechficientCaseRow>;
  }

  async getEligibility(): Promise<TechficientEligibility> {
    return handleApiCall(async () =>
      await api.get<TechficientEligibility>({
        url: getApiUrl(endpoint.techficient.eligibility),
        auth: true,
      }),
    ) as Promise<TechficientEligibility>;
  }

  /**
   * Per-agent commission ledger — every PolicyCommission row + a rollup
   * `balance` keyed by status. Powers the Quote & Enroll KPI hero (Advanced,
   * Charged Back, Earned, Net) and any downstream leaderboard / earnings UI.
   */
  async getMyCommissions(): Promise<TechficientMyCommissionsResponse> {
    return handleApiCall(async () =>
      await api.get<TechficientMyCommissionsResponse>({
        url: getApiUrl(endpoint.techficient.myCommissions),
        auth: true,
      }),
    ) as Promise<TechficientMyCommissionsResponse>;
  }

  /**
   * Agency-wide Techficient rollup for the Commissions tab on the agency
   * dashboard. Scoped to the caller's agency tree (recursive CTE on the
   * backend); a sub-agency owner sees just their slice, the MRFG top-of-tree
   * sees the whole downline.
   */
  async getAgencyDashboard(
    agencyId: string,
  ): Promise<TechficientAgencyDashboardResponse> {
    return handleApiCall(async () =>
      await api.get<TechficientAgencyDashboardResponse>({
        url: getApiUrl(endpoint.techficient.agencyDashboard(agencyId)),
        auth: true,
      }),
    ) as Promise<TechficientAgencyDashboardResponse>;
  }

  /**
   * Single-agent Techficient profile for the drill-down panel on
   * AgentDetailsView — provisioning state, every state license, every carrier
   * contract, recent cases (with policy + commission joined), recent quotes,
   * commission balance.
   */
  async getAgentTechficientDetails(
    userId: string,
  ): Promise<TechficientAgentDetailsResponse> {
    return handleApiCall(async () =>
      await api.get<TechficientAgentDetailsResponse>({
        url: getApiUrl(endpoint.techficient.agentDetails(userId)),
        auth: true,
      }),
    ) as Promise<TechficientAgentDetailsResponse>;
  }

  /**
   * Producer profile — everything the Manage Profile dashboard renders +
   * lets the agent edit (name, contact, address, DOB, NPN, masked SSN).
   * SSN is never round-tripped in plaintext; the server returns only
   * `ssnLastFour` for masked display.
   */
  async getProducerProfile(): Promise<ProducerProfileResponse> {
    return handleApiCall(async () =>
      await api.get<ProducerProfileResponse>({
        url: getApiUrl(endpoint.techficient.profile),
        auth: true,
      }),
    ) as Promise<ProducerProfileResponse>;
  }

  async updateProducerProfile(
    body: Partial<UpdateProducerProfileRequest>,
  ): Promise<ProducerProfileResponse> {
    return handleApiCall(async () =>
      await api.patch<ProducerProfileResponse>({
        url: getApiUrl(endpoint.techficient.profile),
        body,
        auth: true,
      }),
    ) as Promise<ProducerProfileResponse>;
  }

  /**
   * Self-serve onboarding — pushes the agent's NPN + state licenses + carrier
   * writing numbers to the backend, which calls Techficient's `/user/create`
   * (or `/user/update` if they already exist). On success eligibility flips
   * to `provisioned: true` and the wizard swaps out for the normal Quote &
   * Enroll surface.
   */
  async onboard(body: {
    npn: string;
    stateLicenses: Array<{ stateCode: string; licenseNumber: string }>;
    carrierContracts?: Array<{ carrierCode: string; writingNumber: string }>;
  }): Promise<TechficientEligibility> {
    return handleApiCall(async () =>
      await api.post<TechficientEligibility>({
        url: getApiUrl(endpoint.techficient.onboard),
        body,
        auth: true,
      }),
    ) as Promise<TechficientEligibility>;
  }
}

export interface ProducerProfileResponse {
  firstName: string;
  lastName: string;
  name: string;
  email: string | null;
  phone: string | null;
  npn: string | null;
  npnState: string | null;
  dob: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  /** Last 4 digits of SSN — server never returns the full SSN. */
  ssnLastFour: string | null;
}

export interface UpdateProducerProfileRequest {
  firstName: string;
  lastName: string;
  phone: string;
  npn: string;
  dob: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  /**
   * Plaintext SSN — server encrypts with AES-256-GCM before persisting.
   * Never echoed back in any response. Omit when not changing.
   */
  ssn?: string;
}

export interface TechficientAgencyTotals {
  agentCount: number;
  provisionedAgentCount: number;
  quoteCount: number;
  totalCases: number;
  inProgress: number;
  soldApproved: number;
  lapsedDeclined: number;
  appliedFaceTotal: number;
  approvedFaceTotal: number;
  appliedModalPremium: number;
  approvedModalPremium: number;
  commissionAdvanced: number;
  commissionChargedBack: number;
  commissionEarned: number;
  commissionPendingExpected: number;
  commissionNetInPocket: number;
  /** Percentage 0-100, rounded to one decimal. */
  quoteToAppConversion: number;
}

export interface TechficientAgentRollupRow {
  userId: string;
  name: string;
  email: string;
  agencyName: string | null;
  techficientUserId: string | null;
  techficientProvisioned: boolean;
  lastSyncedAt: string | null;
  stateLicenseCount: number;
  carrierContractCount: number;
  quoteCount: number;
  totalCases: number;
  inProgress: number;
  soldApproved: number;
  lapsedDeclined: number;
  appliedFaceTotal: number;
  approvedFaceTotal: number;
  appliedModalPremium: number;
  approvedModalPremium: number;
  commissionAdvanced: number;
  commissionChargedBack: number;
  commissionEarned: number;
  commissionPendingExpected: number;
}

export interface TechficientCarrierBreakdownRow {
  carrierName: string;
  carrierCode: string;
  caseCount: number;
  soldCount: number;
  appliedFaceTotal: number;
  appliedModalPremium: number;
}

export interface TechficientAgencyDashboardResponse {
  agencyTotals: TechficientAgencyTotals;
  agents: TechficientAgentRollupRow[];
  carriers: TechficientCarrierBreakdownRow[];
}

export interface TechficientAgentDetailsResponse {
  user: { id: string; name: string; email: string };
  provisioning: {
    provisioned: boolean;
    techficientUserId: string | null;
    federatedUserId: string | null;
    eligibilityStatus: string | null;
    lastSyncedAt: string | null;
    stateLicenses: Array<{ stateCode: string; licenseNumber: string }>;
    carrierContracts: Array<{
      carrierCode: string;
      writingNumber: string;
      productCode?: string;
    }>;
  };
  cases: TechficientCaseRow[];
  quotes: Array<{
    id: string;
    techficientQuoteRequestId: string;
    leadId: string | null;
    status: string;
    createdAt: string;
    resultCount: number;
  }>;
  commissionBalance: {
    advanced: number;
    chargedBack: number;
    earned: number;
    netPending: number;
  };
}

export interface TechficientMyCommissionsResponse {
  items: Array<
    TechficientCommissionRow & {
      id: string;
      policyId: string;
      userId: string;
      agencyId?: string;
    }
  >;
  balance: {
    advanced: number;
    chargedBack: number;
    earned: number;
    net: number;
  };
}

export const techficientService = new TechficientService();
export default techficientService;
